/** @format */

import {useCallback} from 'react';

import {useCompanyContext} from 'contexts/CompanyContext';
import {useMixpanelContext} from 'contexts/MixpanelContext';
import {AiMonitoringConfigInput} from 'graphql/generated/types';

import {
  useStartAiMonitoringMutation,
  useStopAiMonitoringMutation,
  useUpdateAiMonitoringSettingsMutation,
} from '../../../graphql/__generated__';
import {resolveGoalsWithDetails} from '../../Form/helpers';
import {MonitoringFormState} from './useMonitoringConfigForm';

interface GoalOption {
  id: string;
  name: string;
  counterId: string;
  counterName: string;
}

interface UseMonitoringDataParams {
  refetchData: () => Promise<void>;
  goalOptions: GoalOption[];
}

export const useMonitoringData = ({refetchData, goalOptions}: UseMonitoringDataParams) => {
  const {
    currentCompany: {id: companyId, uuid: companyUuid},
  } = useCompanyContext();
  const {mixpanel} = useMixpanelContext();

  const [startMutation, {loading: startLoading}] = useStartAiMonitoringMutation();
  const [stopMutation, {loading: stopLoading}] = useStopAiMonitoringMutation();
  const [updateMutation, {loading: updateLoading}] = useUpdateAiMonitoringSettingsMutation();

  const buildInput = useCallback(
    (formState: MonitoringFormState): AiMonitoringConfigInput => {
      const {goals: resolvedGoals} = resolveGoalsWithDetails(formState.goals, goalOptions);

      return {
        companyId,
        campaignSelectionMode: formState.campaignSelectionMode,
        campaignIds:
          formState.campaignSelectionMode === 'MANUAL' ? formState.campaigns.map(Number) : null,
        campaignNameMask:
          formState.campaignSelectionMode === 'AUTO' ? formState.campaignNameMask : null,
        goals: resolvedGoals.map(g => ({
          id: g.id,
          name: g.name,
          counterId: g.counterId,
          counterName: g.counterName,
        })),
        attributionModel: formState.attributionModel,
        kpi: formState.kpi as 'CPL' | 'DRR',
        kpiValue: formState.kpiAmount,
        budget: formState.budget || null,
      };
    },
    [companyId, goalOptions],
  );

  const startMonitoring = useCallback(
    async (formState: MonitoringFormState) => {
      mixpanel.track('ai_monitoring_start_click', {project_uuid: companyUuid});
      const input = buildInput(formState);
      await startMutation({variables: {data: input}});
      await refetchData();
    },
    [buildInput, startMutation, refetchData, mixpanel, companyUuid],
  );

  const stopMonitoring = useCallback(async () => {
    mixpanel.track('ai_monitoring_stopped_click', {project_uuid: companyUuid});
    await stopMutation({variables: {companyId}});
    await refetchData();
  }, [stopMutation, companyId, refetchData, mixpanel, companyUuid]);

  const updateSettings = useCallback(
    async (formState: MonitoringFormState) => {
      mixpanel.track('ai_monitoring_update_click', {project_uuid: companyUuid});
      const input = buildInput(formState);
      await updateMutation({variables: {data: input}});
      await refetchData();
    },
    [buildInput, updateMutation, refetchData, mixpanel, companyUuid],
  );

  return {
    startLoading,
    stopLoading,
    updateLoading,
    mutationLoading: startLoading || stopLoading || updateLoading,
    startMonitoring,
    stopMonitoring,
    updateSettings,
  };
};
